export interface ColumnPrecision {
  volume: number
  perUnit: number
  currency: number
}

export interface PrecisionData {
  precisions: ColumnPrecision
  hasData: boolean
}

function getDecimalPlaces(value: number): number {
  if (value === null || value === undefined || !isFinite(value)) return 0

  const str = value.toString()
  if (!str.includes('.')) return 0

  const decimalPart = str.split('.')[1]
  // Remove trailing zeros to get actual decimal places needed
  const trimmed = decimalPart.replace(/0+$/, '')
  return trimmed.length
}

export function calculateColumnPrecisions(data: any[]): PrecisionData {
  if (!data || data.length === 0) {
    return {
      precisions: { volume: 3, perUnit: 6, currency: 2 },
      hasData: false,
    }
  }

  let maxVolumeDecimals = 0
  let maxPerUnitDecimals = 2 // Minimum 2 decimals for per unit
  const currencyDecimals = 2 // Currency always uses 2 decimals

  // Analyze all rows for volume and per unit values
  data.forEach((row) => {
    if (!row) return

    // Check various volume field names
    const volumeFields = ['volume', 'gross_volume', 'net_volume', 'amount']
    volumeFields.forEach((field) => {
      if (row[field] !== null && row[field] !== undefined) {
        const decimals = getDecimalPlaces(row[field])
        maxVolumeDecimals = Math.max(maxVolumeDecimals, Math.min(decimals, 3)) // Max 3 decimals for volume
      }
    })

    // Check per unit field names
    const perUnitFields = ['per_unit', 'rate', 'price_per_gallon']
    perUnitFields.forEach((field) => {
      if (row[field] !== null && row[field] !== undefined) {
        const decimals = getDecimalPlaces(row[field])
        maxPerUnitDecimals = Math.max(maxPerUnitDecimals, Math.min(decimals, 6)) // Max 6 decimals for per unit
      }
    })
  })

  return {
    precisions: {
      volume: maxVolumeDecimals,
      perUnit: maxPerUnitDecimals,
      currency: currencyDecimals,
    },
    hasData: true,
  }
}

export function calculateMatchSummaryPrecisions(summaryData: any[]): PrecisionData {
  if (!summaryData || summaryData.length === 0) {
    return {
      precisions: { volume: 3, perUnit: 6, currency: 2 },
      hasData: false,
    }
  }

  let maxVolumeDecimals = 0
  let maxPerUnitDecimals = 2
  const currencyDecimals = 2

  summaryData.forEach((row) => {
    if (!row) return

    // Check volume fields specific to match summary
    ;['gross_volume', 'net_volume'].forEach((field) => {
      if (row[field] !== null && row[field] !== undefined) {
        const decimals = getDecimalPlaces(row[field])
        maxVolumeDecimals = Math.max(maxVolumeDecimals, Math.min(decimals, 3))
      }
    })

    // Check per unit field
    if (row.per_unit !== null && row.per_unit !== undefined) {
      const decimals = getDecimalPlaces(row.per_unit)
      maxPerUnitDecimals = Math.max(maxPerUnitDecimals, Math.min(decimals, 6))
    }
  })

  return {
    precisions: {
      volume: maxVolumeDecimals,
      perUnit: maxPerUnitDecimals,
      currency: currencyDecimals,
    },
    hasData: true,
  }
}

export function calculateTaxPrecisions(taxData: any[]): PrecisionData {
  if (!taxData || taxData.length === 0) {
    return {
      precisions: { volume: 3, perUnit: 4, currency: 2 },
      hasData: false,
    }
  }

  let maxVolumeDecimals = 0
  let maxPerUnitDecimals = 2
  const currencyDecimals = 2

  taxData.forEach((row) => {
    if (!row) return

    // Tax volume field
    if (row.amount !== null && row.amount !== undefined) {
      const decimals = getDecimalPlaces(row.amount)
      maxVolumeDecimals = Math.max(maxVolumeDecimals, Math.min(decimals, 3))
    }

    // Tax rate field (per unit equivalent)
    if (row.rate !== null && row.rate !== undefined) {
      const decimals = getDecimalPlaces(row.rate)
      maxPerUnitDecimals = Math.max(maxPerUnitDecimals, Math.min(decimals, 4)) // Tax rates typically need fewer decimals
    }
  })

  return {
    precisions: {
      volume: maxVolumeDecimals,
      perUnit: maxPerUnitDecimals,
      currency: currencyDecimals,
    },
    hasData: true,
  }
}

export function calculateManageMatchPrecisions(bolData: any[], invoiceData: any[]): PrecisionData {
  const allData = [...(bolData || []), ...(invoiceData || [])]

  if (allData.length === 0) {
    return {
      precisions: { volume: 3, perUnit: 6, currency: 2 },
      hasData: false,
    }
  }

  let maxVolumeDecimals = 0
  let maxPerUnitDecimals = 2
  const currencyDecimals = 2

  allData.forEach((row) => {
    if (!row) return

    // Volume field
    if (row.volume !== null && row.volume !== undefined) {
      const decimals = getDecimalPlaces(row.volume)
      maxVolumeDecimals = Math.max(maxVolumeDecimals, Math.min(decimals, 3))
    }

    // Per unit equivalent (might be in different fields)
    ;['per_unit', 'rate', 'unit_price'].forEach((field) => {
      if (row[field] !== null && row[field] !== undefined) {
        const decimals = getDecimalPlaces(row[field])
        maxPerUnitDecimals = Math.max(maxPerUnitDecimals, Math.min(decimals, 6))
      }
    })
  })

  return {
    precisions: {
      volume: maxVolumeDecimals,
      perUnit: maxPerUnitDecimals,
      currency: currencyDecimals,
    },
    hasData: true,
  }
}
